// sessionExpiry — the CRM's ONE rule for "this session is over, sign out".
//
// ── THE BUG THIS FILE EXISTS FOR ───────────────────────────────────────────
// An operator's session expired and the CRM stayed on screen. Nothing signed
// them out: the shell rendered, the nav worked, and every panel quietly filled
// with errors. Two independent reasons, both fixed together:
//
//  1. **Nothing watched the clock.** `RopcAuthProvider` renewed the access
//     token on a timer, and when that timer did not fire (tab asleep, machine
//     suspended, the renewal call failing to reach Accounts) the expired token
//     just sat there — and `isAuthenticated` was `token !== null`, so an
//     expired token still read as signed in. Fixed in `@fluxora/auth`'s ropc
//     provider: it now ends the session at `exp` and re-checks on wake.
//
//  2. **Only a 401 ended the session, and not every service sends one.** The
//     api-client factory calls `onUnauthorized` on 401 only (createApiClient.ts
//     :235). **Quotation answers 403 for an expired token** — its verifier maps
//     every `jwtVerify` failure, `"exp" claim timestamp check failed` included,
//     to `NOT_AUTHORISED`, which is HTTP 403 (US/src/middleware/authn.ts:215,
//     api/error-codes.ts:325-330). So on every Quotes screen a dead session
//     produced a red panel and no sign-out, forever. Raw-`fetch` call sites
//     (bill PDF, broker, templates) fire no callback at all, for the same
//     reason.
//
// This module supplies the discriminator both halves need, and `queryClient.ts`
// applies it to EVERY query and mutation in the CRM — one place, so a new
// module inherits the behaviour instead of re-implementing it.
//
// ── WHY THE RULE IS "OUR TOKEN", NOT "THEIR STATUS" ────────────────────────
// A 401/403 from a backend is evidence about THAT CALL, not about our session.
// `apiClient.ts` documents three incidents where treating one as a session
// death logged an operator out mid-task and destroyed unsaved work — a Leads
// tab whose route the deployed service would not authenticate, a template
// validate against a service that does not issue our session. Those advisory
// clients deliberately omit the 401 handler and MUST keep behaving that way.
//
// So the authority here is OUR OWN access token's `exp`, read from the token we
// are holding. If it has expired, the session really is over — no service's
// opinion is needed, and no unsaved work is recoverable anyway because every
// save would fail too. An auth failure while our token is still live is left
// exactly where it was: rendered by the caller, session untouched.
//
// The one exception is spelled out at {@link authFailureEndsSession}.

import { isApiError } from '@fluxora/api-client';

import { env } from '../config/env';

import { getCurrentAuthSnapshot } from './authStore';

/**
 * Treat a token as dead this many seconds before its stated `exp`.
 *
 * Small on purpose. The provider renews 30s early and sweeps at `exp`; this is
 * only here so a token that expires mid-flight is not called live by a call
 * that started a second ago.
 */
const EXPIRY_SKEW_SECONDS = 5;

/** The `exp` claim (epoch seconds) of a JWT, or null when it has none / is not one. */
export function accessTokenExpiry(token: string | null): number | null {
  if (token === null || token.length === 0) return null;
  const segments = token.split('.');
  if (segments.length !== 3) return null; // not a JWT — e.g. the `dev:<tenant>:admin` bearer
  try {
    const payload = segments[1] ?? '';
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(decoded) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * Has the access token the CRM is currently sending expired?
 *
 * `false` when there is no token, or when the bearer is not a JWT (the dev-mode
 * `dev:<tenant>:admin` bridge) — an unreadable expiry is never grounds for
 * ending a session.
 */
export function isAccessTokenExpired(): boolean {
  const exp = accessTokenExpiry(getCurrentAuthSnapshot().token);
  if (exp === null) return false;
  return exp - EXPIRY_SKEW_SECONDS <= Math.floor(Date.now() / 1000);
}

/**
 * A backend's own words for "I could not verify this token", as opposed to "you
 * may not do this". Quotation returns BOTH as 403 `NOT_AUTHORISED`; the detail
 * string is the only thing separating an expired bearer from a role denial.
 *
 * Deliberately narrow — it matches the verifier's message prefix, not the word
 * "token" anywhere in a body, so a business error that happens to mention a
 * token cannot sign anyone out.
 *
 * ⚠️ ONLY MEANINGFUL FROM A SERVICE THAT ISSUES OUR SESSION. This is exported
 * for `apiClient.ts`'s session-owning clients and must NOT be re-applied to
 * every call — see the note in {@link authFailureEndsSession}.
 */
export function isTokenVerificationFailure(detail: string | undefined): boolean {
  if (detail === undefined) return false;
  return /jwt verification failed|invalid[_ ]token|token (has )?expired|expired token/i.test(detail);
}

/** Why the session ended, carried through to the login screen. */
export type SessionEndReason = 'expired' | 'rejected';

/**
 * Does this failure mean the CRM session is over?
 *
 * Returns the reason to sign out with, or `null` to leave the session alone —
 * which is the answer for every ordinary 403 (an operator opening a screen their
 * role does not cover) and every failure that is not an auth failure at all.
 *
 * ⚠️ DEV BUILDS ARE EXEMPT. Dev mode signs requests with the synthetic
 * `dev:<tenant>:admin` bearer, which several deployed services reject outright
 * (see the `accountsFunnelApi` note in apiClient.ts). Those 401s say nothing
 * about a session's health and used to bounce developers to /login on a tab
 * click; a dummy-auth session has no expiry to enforce anyway.
 */
export function authFailureEndsSession(error: unknown): SessionEndReason | null {
  if (env.devMode) return null;
  if (!isApiError(error) || error.kind !== 'auth') return null;
  // Our own clock first — this is the authority, and it is the case that covers
  // a 401 from Accounts, a 403 from Quotation and a raw-fetch PDF download
  // alike, whatever each one chose to call it.
  if (isAccessTokenExpired()) return 'expired';
  // ⚠️ NOTHING ELSE LANDS HERE, ON PURPOSE — NOT A BARE 401, AND NOT A
  // "COULD NOT VERIFY THIS TOKEN" EITHER.
  //
  // This rule runs on EVERY call in the CRM, advisory clients included, and a
  // rejection from a service that does not issue our session is not evidence
  // that our session died. Three logged incidents in apiClient.ts say so, one
  // of which destroyed 85 KB of unsaved work.
  //
  // The verification-failure detail was briefly tested here too, and QA showed
  // why that was wrong (QA-2026-09-17-02): the Broker Portal and the Customer
  // Portal verify against their OWN issuer, so `no applicable key found in the
  // JSON Web Key Set` from one of them means "that deployment was never going
  // to trust our issuer" — a perfectly healthy CRM session — and it was
  // reaching those clients from Settings → Templates *Validate*, i.e. with an
  // unsaved draft on screen. What kept the exemption alive in testing was
  // nothing but the accident that local Accounts' 401 body carries no `detail`.
  //
  // Both of those signals ARE honoured — for the clients that speak for the
  // session — by the wrapper in apiClient.ts `build()`, which is the only layer
  // that knows which service it called.
  return null;
}
